import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { CONTACT_INFO } from "@/lib/pulseConstants";

const LAST_UPDATED = "14 July 2026";

function Section({ title, children, testId }) {
    return (
        <section className="mb-10" data-testid={testId}>
            <h2 className="font-display text-2xl md:text-3xl text-white mb-4 leading-tight">
                {title}
            </h2>
            <div className="space-y-4 body-md">{children}</div>
        </section>
    );
}

export default function PrivacyPage() {
    useEffect(() => {
        document.title = "Privacy Policy \u2014 REFORM Pulse";
    }, []);

    return (
        <div data-testid="privacy-page">
            <section className="relative pt-24 md:pt-28 lg:pt-32 pb-14 border-b border-white/10">
                <div className="container-editorial">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white transition-colors min-h-11 py-3"
                        data-testid="privacy-back-link"
                    >
                        <ArrowLeft size={14} /> Back to Home
                    </Link>
                    <SectionEyebrow index="//" label="Legal" />
                    <Reveal>
                        <h1 className="display-1 text-white text-balance max-w-4xl">
                            Privacy <span className="text-[#FF2A2A]">Policy.</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="body-lg mt-6 max-w-2xl">
                            How REFORM Pulse handles the information you share with us through this website.
                        </p>
                    </Reveal>
                    <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.24em] text-white/50">
                        Last updated: {LAST_UPDATED}
                    </div>
                </div>
            </section>

            <section className="section-pad">
                <div className="container-editorial">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                        <div className="lg:col-span-8">
                            <Section title="About this policy" testId="privacy-about">
                                <p>
                                    This Privacy Policy explains what information REFORM Pulse (“<strong>we</strong>”, “<strong>us</strong>”, “<strong>REFORM Pulse</strong>”) collects when you visit or submit an enquiry through <a href="https://reform-pulse.com" className="text-white underline hover:text-[#FF2A2A]">reform-pulse.com</a>, how that information is used, and the choices you have.
                                </p>
                                <p>
                                    REFORM Pulse is a brand and business division operated by <strong>REFORM Digital Services Sdn Bhd</strong> (“<strong>we</strong>”, “<strong>us</strong>” or “<strong>REFORM Pulse</strong>”). REFORM Digital Services Sdn Bhd is the data controller responsible for the personal information collected through this website.
                                </p>
                                <p>
                                    REFORM Pulse is the live-experience arm of REFORM Group. If you have any questions about this policy, contact us at <a href={`mailto:${CONTACT_INFO.email}`} className="text-white underline hover:text-[#FF2A2A]">{CONTACT_INFO.email}</a>.
                                </p>
                            </Section>

                            <Section title="Information we collect" testId="privacy-info-collected">
                                <p>When you submit an enquiry or brief through our website, we ask you to provide:</p>
                                <ul className="list-disc list-outside pl-5 space-y-1">
                                    <li>Your name</li>
                                    <li>Company or organisation</li>
                                    <li>Email address</li>
                                    <li>Phone or WhatsApp number</li>
                                    <li>Brand or product you represent</li>
                                    <li>Event type</li>
                                    <li>Estimated event date</li>
                                    <li>Estimated budget range</li>
                                    <li>Campaign objective</li>
                                    <li>A brief or message describing the opportunity</li>
                                    <li>Any files or attachments you choose to share, if that option is available</li>
                                </ul>
                                <p>We also automatically record:</p>
                                <ul className="list-disc list-outside pl-5 space-y-1">
                                    <li>The time and date of your submission</li>
                                    <li>The page you submitted from</li>
                                    <li>Basic technical information reasonably required to operate and secure the website (for example: browser type, general device information, and rate-limit signals used to prevent abuse of the enquiry form)</li>
                                </ul>
                                <p>You are not required to provide any information to browse the website. Information is only collected when you choose to submit it.</p>
                                <p>
                                    Fields marked as required are necessary for us to review and respond to your enquiry. If you do not provide the required information, we may be unable to process or respond to your submission. Other information is optional unless clearly stated otherwise.
                                </p>
                            </Section>

                            <Section title="How we use your information" testId="privacy-usage">
                                <p>We use the information you send us to:</p>
                                <ul className="list-disc list-outside pl-5 space-y-1">
                                    <li>Respond to your enquiry and follow up on the opportunity you have shared</li>
                                    <li>Prepare a first point of view, proposal or scope where appropriate</li>
                                    <li>Manage the ongoing relationship if we begin working together</li>
                                    <li>Operate, secure and improve the website (for example, to reduce spam submissions and monitor form performance)</li>
                                </ul>
                                <p>
                                    We do not sell your personal information, and we do not share it with unrelated third parties for their own marketing.
                                </p>
                            </Section>

                            <Section title="Where your information is stored and who processes it" testId="privacy-storage">
                                <p>To operate the enquiry pipeline, submissions may be:</p>
                                <ul className="list-disc list-outside pl-5 space-y-1">
                                    <li><strong>Stored in the website’s primary database</strong>, which is the authoritative record of your enquiry.</li>
                                    <li><strong>Synchronised to Airtable</strong>, which we use as an internal lead-management workspace.</li>
                                    <li><strong>Processed through SendGrid</strong>, which delivers internal notifications to REFORM Pulse and, where applicable, a confirmation email to you.</li>
                                    <li><strong>Accessed by authorised REFORM Pulse and REFORM Group personnel</strong> on a need-to-know basis in order to respond to your enquiry.</li>
                                </ul>
                                <p>
                                    These processors may store or transmit information outside Malaysia. We select established service providers with reasonable technical and organisational safeguards.
                                </p>
                            </Section>

                            <Section title="Cookies and analytics" testId="privacy-cookies">
                                <p>
                                    We may use a limited number of cookies and similar technologies for the following purposes:
                                </p>
                                <ul className="list-disc list-outside pl-5 space-y-1">
                                    <li>To make the website function correctly (essential cookies)</li>
                                    <li>To understand aggregate visitor behaviour so we can improve the site (analytics)</li>
                                    <li>To measure the effectiveness of campaigns that direct visitors to the site (marketing / advertising measurement)</li>
                                </ul>
                                <p>
                                    Analytics and advertising measurement tools may include, at various times, services such as Google Analytics 4, Meta Pixel or similar. When these are active they may set their own cookies and receive limited information about your visit, such as pages viewed and device information. Where required by law, we will only enable these tools with your consent through a cookie notice or similar mechanism.
                                </p>
                                <p>
                                    You can control cookies through your browser settings. Blocking or deleting cookies may affect how the website works.
                                </p>
                            </Section>

                            <Section title="How long we keep your information" testId="privacy-retention">
                                <p>
                                    We keep enquiry information for as long as reasonably necessary to respond to and act on the opportunity, to maintain a record of communications for reference, and to comply with any applicable legal, tax or accounting obligations. If we do not proceed to work together, we will delete or anonymise your enquiry within a reasonable period once it is no longer needed.
                                </p>
                                <p>
                                    You can also request earlier deletion at any time — see below.
                                </p>
                            </Section>

                            <Section title="Security" testId="privacy-security">
                                <p>
                                    We use reasonable technical and organisational measures designed to protect the information you share with us, including access controls, encrypted transmission of form submissions, and rate-limiting on the enquiry form to reduce automated abuse.
                                </p>
                                <p>
                                    No online transmission or storage is ever completely secure, and we cannot guarantee absolute security. If you believe your information has been misused, please contact us at <a href={`mailto:${CONTACT_INFO.email}`} className="text-white underline hover:text-[#FF2A2A]">{CONTACT_INFO.email}</a>.
                                </p>
                            </Section>

                            <Section title="Your rights: access, correction and deletion" testId="privacy-rights">
                                <p>
                                    Subject to applicable law, you can ask us to:
                                </p>
                                <ul className="list-disc list-outside pl-5 space-y-1">
                                    <li>Confirm what information about you we hold in connection with your enquiry</li>
                                    <li>Correct information that is inaccurate or incomplete</li>
                                    <li>Delete your enquiry record, subject to any legal or operational retention obligation</li>
                                    <li>Restrict or object to certain uses of your information</li>
                                    <li>Withdraw consent at any time where our processing relies on your consent, without affecting the lawfulness of processing carried out before withdrawal</li>
                                    <li>Object to, or opt out of, direct marketing communications from us at any time</li>
                                </ul>
                                <p>
                                    To make a request, email <a href={`mailto:${CONTACT_INFO.email}`} className="text-white underline hover:text-[#FF2A2A]">{CONTACT_INFO.email}</a> from the address you used to contact us, or provide enough detail for us to reasonably identify your record. We will respond within a reasonable timeframe. Please note that some requests, in particular deletion, may not be fully actioned where we are legally required or operationally obliged to retain certain records (for example, to comply with tax, accounting or audit obligations, or to resolve disputes and enforce agreements).
                                </p>
                            </Section>

                            <Section title="Children" testId="privacy-children">
                                <p>
                                    This website is directed at business professionals and is not intended for children. We do not knowingly collect information from anyone under 18.
                                </p>
                            </Section>

                            <Section title="Changes to this policy" testId="privacy-changes">
                                <p>
                                    We may update this Privacy Policy from time to time. When we do, we will update the “Last updated” date at the top of this page. Material changes will be reflected clearly in this policy.
                                </p>
                            </Section>

                            <Section title="Contact" testId="privacy-contact">
                                <p>
                                    For any privacy-related question or request, contact <strong>REFORM Digital Services Sdn Bhd</strong> at:
                                </p>
                                <p>
                                    <a
                                        href={`mailto:${CONTACT_INFO.email}`}
                                        className="inline-flex items-center gap-2 text-white hover:text-[#FF2A2A] transition-colors min-h-11 py-2"
                                        data-testid="privacy-email-link"
                                    >
                                        <Mail size={16} /> {CONTACT_INFO.email}
                                    </a>
                                </p>
                            </Section>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
